import React, { FunctionComponent, useEffect } from "react"
import { CircularProgress, Grid, TextField, Typography } from "@material-ui/core"
import { DownloadButton } from "./DownloadButton"
import { useQuery } from "@apollo/client"
import GET_PROMPT_CARD_DETAILS from "../../queries/GET_PROMPT_CARD_DETAILS"
import { Prompt } from "../../../typings/types"
import { AudioPlayer } from "../../components/AudioPlayer"
import { DownloadHelpers } from "../../common/DownloadHelpers"
import styles from "./PromptCard.module.scss"
import { useNotification } from "../../common/hooks/useNotification"

interface PromptCardProps {
    mediaUUID: string
    hideCard: () => void
}
export const PromptCard: FunctionComponent<PromptCardProps> = (props: PromptCardProps) => {
    const { mediaUUID, hideCard } = props
    const { loading, error, data } = useQuery<{ getPrompt: Prompt }>(GET_PROMPT_CARD_DETAILS, {
        variables: { mediaUUID: mediaUUID }
    })
    const { notifyError } = useNotification()

    useEffect(() => {
        if (error) {
            notifyError("Error loading file")
            hideCard()
        }
    }, [error, notifyError, hideCard])

    const languagesAsString =
        data?.getPrompt.languages?.map(l => l.name).join(", ") || "No Language"

    return (
        <Grid
            container
            direction="row"
            spacing={2}
            justify="center"
            data-testid="promptCardData"
            className={styles.PromptsCard__card}
        >
            {loading && (
                <Grid item>
                    <CircularProgress data-testid="promptCardLoading" />
                </Grid>
            )}
            {data && (
                <>
                    <Grid
                        id="promptCardDetails"
                        item
                        container
                        spacing={4}
                        xs={6}
                        direction="column"
                        justify="center"
                        alignItems="center"
                    >
                        <Grid
                            item
                            container
                            spacing={4}
                            justify="center"
                            direction="column"
                            className={styles.PromptsCard__details__container}
                        >
                            <Grid
                                item
                                container
                                justify="flex-start"
                                className={styles.PromptsCard__details__item}
                            >
                                <Grid item className={styles.PromptsCard__label}>
                                    <Typography>Filename:</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography>{data.getPrompt.fileName}</Typography>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                justify="flex-start"
                                className={styles.PromptsCard__details__item}
                            >
                                <Grid item className={styles.PromptsCard__label}>
                                    <Typography>Languages:</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography>{languagesAsString}</Typography>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                justify="flex-start"
                                className={styles.PromptsCard__details__item}
                            >
                                <Grid item>
                                    <AudioPlayer
                                        playerId={`player-${data?.getPrompt.id}`}
                                        audioFile={data?.getPrompt?.playableAudio}
                                        loading={loading}
                                        error={error}
                                    />
                                </Grid>
                                <Grid item className={styles.PromptsCard__download__button}>
                                    <DownloadButton
                                        id={`download-${data?.getPrompt.id}`}
                                        fileName={data.getPrompt.fileName}
                                        blob={DownloadHelpers.getAudioBlob(
                                            data?.getPrompt?.downloadableAudio?.binaryData
                                        )}
                                        visible={true}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item id="promptCardText" xs={6}>
                        <TextField
                            label={"Transcription"}
                            InputLabelProps={{ shrink: true }}
                            value={data.getPrompt.originalText}
                            className={styles.PromptsCard__text}
                            rows={10}
                            variant="outlined"
                            multiline
                            InputProps={{
                                readOnly: true
                            }}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
}
